/**
 * Centralized storage utility for work-related data.
 * Uses localStorage for persistence across tabs and page refreshes.
 * All storage keys include workId to prevent data leaking between different works.
 */

// Storage key prefixes
const STORAGE_KEYS = {
    ATTACHMENTS: 'AttachmentUpload_Files',
    PR_LIST: 'CodingWork_PrList',
    REVIEW_STATUS: 'ReviewStatus_',
    WORKFLOW_STATUS: 'WorkflowStatus_',
    INACTIVITY_PING: 'InactivityPing_',
    LAST_SEEN_WORK: 'LastSeenWork'
} as const;

// ============================================
// Attachments Storage
// ============================================

export interface StoredAttachment {
    id: number;
    fileId?: number;
    fileName: string;
    fileType: string;
    fileSize: number;
    status?: 'uploading' | 'completed' | 'error';
    uploadProgress?: number;
}

export const attachmentsStorage = {
    getKey: (workId: number): string => `${STORAGE_KEYS.ATTACHMENTS}_${workId}`,
    
    save: (workId: number, attachments: StoredAttachment[]): void => {
        try {
            if (attachments.length > 0) {
                localStorage.setItem(
                    attachmentsStorage.getKey(workId),
                    JSON.stringify(attachments)
                );
            } else {
                attachmentsStorage.clear(workId);
            }
        } catch (error) {
            console.error('Error saving attachments to localStorage:', error);
        }
    },
    
    load: (workId: number): StoredAttachment[] | null => {
        try {
            const data = localStorage.getItem(attachmentsStorage.getKey(workId));
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Error loading attachments from localStorage:', error);
            return null;
        }
    },
    
    clear: (workId: number): void => {
        try {
            localStorage.removeItem(attachmentsStorage.getKey(workId));
        } catch (error) {
            console.error('Error clearing attachments from localStorage:', error);
        }
    }
};

// ============================================
// PR List Storage
// ============================================

export const prListStorage = {
    getKey: (workId: number): string => `${STORAGE_KEYS.PR_LIST}_${workId}`,
    
    save: (workId: number, prList: string[]): void => {
        try {
            localStorage.setItem(
                prListStorage.getKey(workId),
                JSON.stringify(prList)
            );
        } catch (error) {
            console.error('Error saving PR list to localStorage:', error);
        }
    },
    
    load: (workId: number): string[] | null => {
        try {
            const data = localStorage.getItem(prListStorage.getKey(workId));
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Error loading PR list from localStorage:', error);
            return null;
        }
    },
    
    clear: (workId: number): void => {
        try {
            localStorage.removeItem(prListStorage.getKey(workId));
        } catch (error) {
            console.error('Error clearing PR list from localStorage:', error);
        }
    }
};

// ============================================
// Review Status Storage
// ============================================

export interface StoredReviewStatus {
    questReviewFeedback: string;
    approveDialogOpen: boolean;
    questReviewDialogOpen: boolean;
}

export const reviewStatusStorage = {
    getKey: (workId: number): string => `${STORAGE_KEYS.REVIEW_STATUS}${workId}`,
    
    save: (workId: number, partialStatus: Partial<StoredReviewStatus>): void => {
        try {
            const currentStatus = reviewStatusStorage.load(workId);
            localStorage.setItem(
                reviewStatusStorage.getKey(workId),
                JSON.stringify({ ...currentStatus, ...partialStatus })
            );
        } catch (error) {
            console.error('Error saving review status to localStorage:', error);
        }
    },
    
    load: (workId: number): StoredReviewStatus => {
        try {
            const data = localStorage.getItem(reviewStatusStorage.getKey(workId));
            if (data) {
                return JSON.parse(data);
            }
        } catch (error) {
            console.error('Error loading review status from localStorage:', error);
        }
        // Return default values if no data or error
        return {
            questReviewFeedback: '',
            approveDialogOpen: false,
            questReviewDialogOpen: false
        };
    },
    
    clear: (workId: number): void => {
        try {
            localStorage.removeItem(reviewStatusStorage.getKey(workId));
        } catch (error) {
            console.error('Error clearing review status from localStorage:', error);
        }
    }
};

// ============================================
// Workflow Status Storage
// ============================================

export interface StoredWorkflowStatus {
    workflowsPassed?: boolean;
    workflowsFeedback?: any;
    isAnalyzingWorkflows?: boolean;
}

export const workflowStatusStorage = {
    getKey: (workId: number): string => `${STORAGE_KEYS.WORKFLOW_STATUS}${workId}`,
    
    save: (workId: number, status: StoredWorkflowStatus): void => {
        try {
            localStorage.setItem(
                workflowStatusStorage.getKey(workId),
                JSON.stringify(status)
            );
        } catch (error) {
            console.error('Error saving workflow status to localStorage:', error);
        }
    },
    
    load: (workId: number): StoredWorkflowStatus | null => {
        try {
            const data = localStorage.getItem(workflowStatusStorage.getKey(workId));
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Error loading workflow status from localStorage:', error);
            return null;
        }
    },
    
    clear: (workId: number): void => {
        try {
            localStorage.removeItem(workflowStatusStorage.getKey(workId));
        } catch (error) {
            console.error('Error clearing workflow status from localStorage:', error);
        }
    }
};

// ============================================
// Inactivity Ping Storage
// ============================================

export interface StoredInactivityPing {
    pingDialogOpen: boolean;
    pingNumber: number;
}

export const inactivityPingStorage = {
    getKey: (workId: number): string => `${STORAGE_KEYS.INACTIVITY_PING}${workId}`,

    save: (workId: number, status: StoredInactivityPing): void => {
        try {
            localStorage.setItem(
                inactivityPingStorage.getKey(workId),
                JSON.stringify(status)
            );
        } catch (error) {
            console.error('Error saving inactivity ping to localStorage:', error);
        }
    },

    load: (workId: number): StoredInactivityPing => {
        try {
            const data = localStorage.getItem(inactivityPingStorage.getKey(workId));
            if (data) {
                return JSON.parse(data);
            }
        } catch (error) {
            console.error('Error loading inactivity ping from localStorage:', error);
        }
        return { pingDialogOpen: false, pingNumber: 1 };
    },

    clear: (workId: number): void => {
        try {
            localStorage.removeItem(inactivityPingStorage.getKey(workId));
        } catch (error) {
            console.error('Error clearing inactivity ping from localStorage:', error);
        }
    }
};

// ============================================
// Last seen work (restore on refresh)
// ============================================

export const lastSeenWorkStorage = {
    getKey: (): string => {
        const userId = localStorage.getItem('user_id');
        return userId
            ? `${STORAGE_KEYS.LAST_SEEN_WORK}_${userId}`
            : STORAGE_KEYS.LAST_SEEN_WORK;
    },

    save: (workId: number): void => {
        try {
            localStorage.setItem(lastSeenWorkStorage.getKey(), String(workId));
        } catch (error) {
            console.error('Error saving last seen work to localStorage:', error);
        }
    },

    load: (): number | undefined => {
        try {
            const data = localStorage.getItem(lastSeenWorkStorage.getKey());
            if (!data) {
                return undefined;
            }
            const parsed = parseInt(data, 10);
            return Number.isFinite(parsed) ? parsed : undefined;
        } catch (error) {
            console.error('Error loading last seen work from localStorage:', error);
            return undefined;
        }
    },

    clear: (): void => {
        try {
            localStorage.removeItem(lastSeenWorkStorage.getKey());
        } catch (error) {
            console.error('Error clearing last seen work from localStorage:', error);
        }
    }
};

// ============================================
// Utility to clear all work-related storage
// ============================================

export const clearAllWorkStorage = (workId: number): void => {
    attachmentsStorage.clear(workId);
    prListStorage.clear(workId);
    reviewStatusStorage.clear(workId);
    workflowStatusStorage.clear(workId);
    inactivityPingStorage.clear(workId);
};

